import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { importRowReason } from '../../utils/supplyAliasCopy';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { ClipboardCheck, Loader2 } from 'lucide-react';
import { useLocation } from '@/contexts/LocationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { toast } from '@/hooks/use-toast';
import { trpc } from '@/lib/trpc';
import { BULK_STOCKTAKE_MAX_ROWS } from '@threadheads/shared';
import { useInventoryQueries } from '../../hooks/useInventoryQueries';
import { useConnectedClients } from '../../hooks/useConnectedClients';
import BulkUploadErrorBoundary from '@/components/csv-import-wizard/BulkUploadErrorBoundary';
import StepIndicator from '@/components/csv-import-wizard/StepIndicator';
import UploadStep from './steps/UploadStep';
import ValidationStep from './steps/ValidationStep';
import PreviewStep from './steps/PreviewStep';
import ProcessingStep from './steps/ProcessingStep';
import ResultStep from './steps/ResultStep';
import { useBulkStocktakeUpload } from './useBulkStocktakeUpload';

const STOCKTAKE_STEPS = [
  { key: 'upload', label: 'Upload' },
  { key: 'validation', label: 'Validate' },
  { key: 'preview', label: 'Preview' },
  { key: 'processing', label: 'Apply' },
  { key: 'result', label: 'Done' },
];

interface BulkStocktakeWizardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const BulkStocktakeWizardModal: React.FC<BulkStocktakeWizardModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation('inventory');
  const { selectedLocation } = useLocation();
  const { organization } = useOrganization();
  // BLU-1189: gated on `isOpen`. This wizard stays mounted with the rest of
  // the table chrome, and its key carries no production-type filter, so an
  // ungated catalogue here is a second full fetch the moment the operator
  // filters the Inventory page. ValidationStep decides SKU existence off
  // `products`, so the step content below waits for the catalogue rather than
  // marking every row "not stocked at this location".
  const { products, loading: productsLoading } = useInventoryQueries(undefined, {
    enabled: isOpen,
  });
  const store = useBulkStocktakeUpload();
  const utils = trpc.useUtils();

  // Org-wide SKU list for cost lookup
  const allSkusQuery = trpc.inventory.listSkus.useQuery(
    {},
    { staleTime: 60_000, enabled: isOpen },
  );
  const allSkus = allSkusQuery.data ?? [];
  const catalogueLoading = productsLoading || allSkusQuery.isLoading;

  // Owner-cell resolution: the Owner column in a count sheet is a display
  // NAME (own org or a connected client at this shared 3PL location), not an
  // id — resolve it against the same roster the rest of the Inventory
  // surface uses.
  const { clients: connectedClients } = useConnectedClients({
    locationId: selectedLocation?.id,
  });

  const ownerNameToOrgId = useMemo(() => {
    const map = new Map<string, string>();
    if (organization) map.set(organization.name.trim().toLowerCase(), organization.id);
    connectedClients.forEach((client) => {
      map.set(client.name.trim().toLowerCase(), client.id);
    });
    return map;
  }, [organization, connectedClients]);

  // -----------------------------------------------------------------------
  // Mutations
  // -----------------------------------------------------------------------
  const bulkStocktakeMutation = trpc.inventory.bulkStocktake.useMutation();
  const undoBulkStocktakeMutation = trpc.inventory.undoBulkStocktake.useMutation();

  // Reset store on unmount to prevent stale state
  useEffect(() => () => { useBulkStocktakeUpload.getState().reset(); }, []);

  // -----------------------------------------------------------------------
  // Submit handler — triggered from PreviewStep
  // -----------------------------------------------------------------------
  const handleSubmit = useCallback(async () => {
    if (!selectedLocation) return;

    const { previewRows, parsedRows, sourceRows, file } = store;

    // Location column guard: block the whole file if any row was counted at
    // a DIFFERENT location than the one currently selected. Catching this at
    // final submit (rather than earlier) is deliberate — it is the last gate
    // before anything is written.
    const selectedLocationName = selectedLocation.name.trim().toLowerCase();
    for (let i = 0; i < parsedRows.length; i++) {
      const row = parsedRows[i];
      if (!row) continue;
      const rowLocation = row.location.trim();
      if (rowLocation !== '' && rowLocation.toLowerCase() !== selectedLocationName) {
        store.setResult({
          batchId: '',
          successCount: 0,
          failedCount: previewRows.length,
          validationErrors: [{
            row: sourceRows[i] ?? i + 2,
            sku: row.sku,
            reason: t('bulkStocktake.err.locationMismatch', {
              line: sourceRows[i] ?? i + 2,
              location: rowLocation,
            }),
          }],
          executionErrors: [],
          mode: 'blocked',
        });
        return;
      }
    }

    // Owner-cell resolution already happened in ValidationStep: every
    // previewRow carries a resolved `targetOrganizationId` (an unresolved
    // Owner cell is rejected as a per-row error before a row ever reaches
    // preview, so there is nothing left to re-resolve here). Omit the field
    // when it equals the caller's own org, to keep single-tenant submissions
    // byte-identical to before this org was introduced.
    const rows: Array<{ sku: string; newStock: number; targetOrganizationId?: string }> = previewRows.map(
      (previewRow) =>
        previewRow.targetOrganizationId === organization?.id
          ? { sku: previewRow.sku, newStock: previewRow.newStock }
          : { sku: previewRow.sku, newStock: previewRow.newStock, targetOrganizationId: previewRow.targetOrganizationId }
    );

    // BLU-711 A2: the blanks travel too, but ONLY as metadata. On the direct
    // write path the service ignores them entirely (blank is never written,
    // same as today); on the review path they become `counted_qty: null` lines
    // so the approver sees "31 not counted" alongside what WAS counted, rather
    // than a silent gap. Owner cells here always resolve — an unresolvable
    // Owner is a hard per-row error in ValidationStep, so it never reaches the
    // not-counted bucket.
    const uncountedRows = store.notCountedRows
      .slice(0, BULK_STOCKTAKE_MAX_ROWS)
      .map((row) => {
        const targetOrganizationId =
          row.ownerId?.trim() || ownerNameToOrgId.get(row.owner.trim().toLowerCase());
        return targetOrganizationId && targetOrganizationId !== organization?.id
          ? { sku: row.sku, targetOrganizationId }
          : { sku: row.sku };
      });

    store.startProcessing(rows.length);

    try {
      const result = await bulkStocktakeMutation.mutateAsync({
        locationId: selectedLocation.id,
        filename: file?.name,
        // Operator reference for the adjustment-document header.
        ...(store.reference.trim() ? { reference: store.reference.trim() } : {}),
        rows,
        ...(uncountedRows.length > 0 ? { uncountedRows } : {}),
      });

      // Invalidate inventory queries so stock levels refresh
      void utils.inventory.getProducts.invalidate({ locationId: selectedLocation.id });

      // Normalize execution errors from server format { sku, reason } to strings
      // Phase 92: a `supply_alias` row carries the translated reason.
      const execErrors = (result.executionErrors ?? []).map(
        (e: string | { sku: string; reason: string; reasonCode?: string }) =>
          typeof e === 'string' ? e : `${e.sku}: ${importRowReason(t, e)}`
      );

      const proposals = result.proposals ?? [];

      if (result.success) {
        store.setResult({
          batchId: result.batchId ?? '',
          successCount: result.successCount,
          failedCount: result.failedCount,
          validationErrors: result.validationErrors ?? [],
          executionErrors: execErrors,
          mode: result.mode,
          proposals,
        });

        if (result.failedCount === 0) {
          // A count that went for approval has not changed anything yet, so it
          // gets a statement of fact, not a completion. A MIXED sheet says both
          // halves: some rows really did apply.
          const stagedLines = proposals.reduce((sum, p) => sum + p.lineCount, 0);
          const appliedAnything = !!result.batchId && result.successCount > 0;
          toast(
            proposals.length > 0
              ? appliedAnything
                ? {
                    title: t('bulkStocktake.toast.mixedTitle'),
                    description: t('bulkStocktake.toast.mixedDesc', {
                      count: stagedLines,
                      applied: result.successCount,
                      staged: stagedLines,
                    }),
                  }
                : {
                    title: t('bulkStocktake.toast.sentTitle'),
                    description: t('bulkStocktake.toast.sentDesc', { count: stagedLines }),
                  }
              : {
                  title: 'Stocktake complete',
                  description: `Stocktake applied to ${result.successCount} items.`,
                },
          );
          onSuccess();
        } else {
          toast({
            title: 'Stocktake completed with errors',
            description: `${result.successCount} succeeded, ${result.failedCount} failed.`,
            variant: 'destructive',
          });
        }
      } else {
        // Blocked by validation errors from the server
        store.setResult({
          batchId: '',
          successCount: 0,
          failedCount: result.failedCount,
          validationErrors: result.validationErrors ?? [],
          executionErrors: execErrors,
          mode: 'blocked',
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      const isNetworkError =
        message.includes('Failed to fetch') ||
        message.includes('NetworkError') ||
        message.includes('timeout') ||
        message.includes('ECONNREFUSED');

      store.setResult({
        batchId: '',
        successCount: 0,
        failedCount: rows.length,
        validationErrors: [],
        executionErrors: [message],
        mode: isNetworkError ? 'network_error' : undefined,
      });

      toast({
        title: isNetworkError ? 'Connection lost' : 'Stocktake failed',
        description: message,
        variant: 'destructive',
      });
    }
  }, [
    selectedLocation,
    store,
    bulkStocktakeMutation,
    utils.inventory.getProducts,
    onSuccess,
    organization,
    ownerNameToOrgId,
    t,
  ]);

  // -----------------------------------------------------------------------
  // Close handler
  // -----------------------------------------------------------------------
  const handleClose = useCallback(() => {
    store.reset();
    onClose();
  }, [store, onClose]);

  // -----------------------------------------------------------------------
  // Upload another — reset everything
  // -----------------------------------------------------------------------
  const handleUploadAnother = useCallback(() => {
    store.reset();
  }, [store]);

  // -----------------------------------------------------------------------
  // Go back to validation step (from blocked result)
  // -----------------------------------------------------------------------
  const handleGoBack = useCallback(() => {
    store.clearResult();
    store.setStep('validation');
  }, [store]);

  // -----------------------------------------------------------------------
  // Undo handler — triggered from ResultStep
  // -----------------------------------------------------------------------
  const handleUndo = useCallback(async () => {
    if (!selectedLocation || !store.result?.batchId) return;

    try {
      await undoBulkStocktakeMutation.mutateAsync({
        locationId: selectedLocation.id,
        batchId: store.result.batchId,
      });

      void utils.inventory.getProducts.invalidate({ locationId: selectedLocation.id });

      toast({
        title: 'Undo complete',
        description: 'All stock changes from this stocktake have been reversed.',
      });

      onSuccess();
      handleClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast({
        title: 'Undo failed',
        description: message,
        variant: 'destructive',
      });
    }
  }, [selectedLocation, store.result, undoBulkStocktakeMutation, utils, onSuccess, handleClose]);

  // -----------------------------------------------------------------------
  // Prevent closing during processing
  // -----------------------------------------------------------------------
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && store.step !== 'processing') {
        handleClose();
      }
    },
    [store.step, handleClose]
  );

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <BulkUploadErrorBoundary onReset={() => store.reset()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-emerald-600" />
              {t('bulkStocktake.title')}
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file with SKUs and exact stock counts from a physical stocktake
            </DialogDescription>
          </DialogHeader>

          {/* Step indicator */}
          <div className="py-2">
            <StepIndicator
              steps={STOCKTAKE_STEPS}
              currentStep={store.step}
              accentColor="emerald"
            />
          </div>

          {/* Step content */}
          <div className="min-h-[200px]">
            {catalogueLoading ? (
              <div className="flex flex-col items-center justify-center gap-3 py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
                <p className="text-sm text-muted-foreground">
                  {t('bulkStocktake.loadingProducts')}
                </p>
              </div>
            ) : (
              <>
                {store.step === 'upload' && <UploadStep />}

                {store.step === 'validation' && (
                  <ValidationStep
                    products={products}
                    allSkus={allSkus}
                    ownerNameToOrgId={ownerNameToOrgId}
                    ownOrgId={organization?.id ?? ''}
                  />
                )}

                {store.step === 'preview' && (
                  <PreviewStep onSubmit={handleSubmit} />
                )}

                {store.step === 'processing' && <ProcessingStep />}

                {store.step === 'result' && (
                  <ResultStep
                    onUploadAnother={handleUploadAnother}
                    onUndo={handleUndo}
                    isUndoing={undoBulkStocktakeMutation.isPending}
                    onClose={handleClose}
                    onGoBack={handleGoBack}
                  />
                )}
              </>
            )}
          </div>
        </BulkUploadErrorBoundary>
      </DialogContent>
    </Dialog>
  );
};

export default BulkStocktakeWizardModal;
